import { useContext, useEffect, useRef, useState } from "react"
import firebase from "firebase"
import moment from "moment"
import { NavLink } from "react-router-dom"

import { db, funEU } from "../../utils/firebase"
import { AuthContext } from "../../utils/providers/auth"
import { AlertsContext } from "../../utils/providers/alerts"

import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import Table from "../../components/structure/table/table"
import Button from "../../components/ui/button/button"
import Datepicker from "../../components/ui/datepicker/datepicker"
import Select from "../../components/ui/select/select"
import Badge from "../../components/ui/badge/badge"
import CreateClientPaymentLink from "../../components/payments/create-client-payment-link/create-client-payment-link"
import PaymentLinkDetail from "../../components/payments/payment-link-detail/payment-link-detail"
import ApplyCreditToBooking from "../../components/payments/apply-credit-to-booking/apply-credit-to-booking"
import { PlusIcon } from "../../utils/svgs"

import styles from "./payment-links.module.scss"

const resendCallable = funEU.httpsCallable("resendClientPaymentLink")
const cancelCallable = funEU.httpsCallable("cancelClientPaymentLink")

const formatGbp = (value) =>
	Number(value || 0).toLocaleString("en-GB", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})

const STATUS_META = {
	ACTIVE: { label: "Awaiting payment", type: "AMBER" },
	PAID: { label: "Paid", type: "POSITIVE" },
	ALLOCATED: { label: "Allocated", type: "INFO" },
	CANCELLED: { label: "Cancelled", type: "" },
}

const tsToMoment = (ts) => {
	if (!ts) return null
	if (typeof ts.seconds === "number") return moment(ts.seconds, "X")
	if (ts._seconds) return moment(ts._seconds, "X")
	return null
}

/**
 * Payment Links report — lists all standalone client-level payment links
 * created in a date range. Non-admins see their own; admins can pick an
 * agent from the dropdown to see anyone's.
 *
 * Date range defaults to the current month (matching commission report's
 * convention, with the bonus of being a sensible default).
 */
export default function PaymentLinks() {
	const { user } = useContext(AuthContext)
	const { pushAlert } = useContext(AlertsContext)

	const isAdmin = Boolean(user?.is_admin || user?.is_super_admin)

	const [startDate, setStartDate] = useState(moment().startOf("month").valueOf())
	const [endDate, setEndDate] = useState(moment().endOf("month").valueOf())
	const [agents, setAgents] = useState({})
	const [selectedAgent, setSelectedAgent] = useState(null)
	const [rows, setRows] = useState([])
	const [loading, setLoading] = useState(false)
	const [viewingRow, setViewingRow] = useState(null)
	const [allocatingRow, setAllocatingRow] = useState(null)
	const [creating, setCreating] = useState(false)
	const [busyId, setBusyId] = useState(null)
	const [busyAction, setBusyAction] = useState(null)
	// Cached uid → display name lookups so we don't re-fetch the user doc
	// on every snapshot update. Persists across renders via ref.
	const userNameCacheRef = useRef({})

	// Default the agent filter to the current user, but ONLY on the first
	// time the user loads. If we re-defaulted whenever selectedAgent fell
	// back to null we'd immediately undo a user's "Clear agent" click.
	const initialisedRef = useRef(false)
	useEffect(() => {
		if (initialisedRef.current) return
		if (!user?.uid) return
		setSelectedAgent(user.uid)
		initialisedRef.current = true
	}, [user])

	// Load the agent list once (admins only). Make sure the current user
	// is in the list even if they aren't flagged as a commission agent —
	// otherwise the default selection (themselves) would render blank.
	// Sorted alphabetically by name so the dropdown is easy to scan.
	useEffect(() => {
		if (!isAdmin) return
		db.collection("users")
			.where("show_for.targets", "==", true)
			.get()
			.then((snap) => {
				const collected = []
				snap.forEach((doc) => {
					const data = doc.data()
					const name = `${data.first_name || ""} ${data.last_name || ""}`.trim() || doc.id
					collected.push({ uid: doc.id, name })
				})

				if (user?.uid && !collected.some((a) => a.uid === user.uid)) {
					const selfName = `${user.first_name || ""} ${user.last_name || ""}`.trim() || "Me"
					collected.push({ uid: user.uid, name: selfName })
				}

				collected.sort((a, b) => a.name.localeCompare(b.name, "en-GB", { sensitivity: "base" }))

				const next = {}
				collected.forEach(({ uid: id, name }) => {
					next[id] = name
				})

				setAgents(next)
			})
	}, [isAdmin, user])

	// Snapshot listener so the table stays live — new links written by the
	// modal appear automatically, no manual refresh needed. Cleanup on
	// filter change. Admins can clear `selectedAgent` to see every agent's
	// links in the range; non-admins always need their own uid set.
	useEffect(() => {
		if (!startDate || !endDate) return
		if (!isAdmin && !selectedAgent) return

		setLoading(true)
		setRows([])

		const startTs = firebase.firestore.Timestamp.fromMillis(moment(startDate).startOf("day").valueOf())
		const endTs = firebase.firestore.Timestamp.fromMillis(moment(endDate).endOf("day").valueOf())

		let query = db.collectionGroup("payment_links").where("sent_at", ">=", startTs).where("sent_at", "<=", endTs)
		if (selectedAgent) {
			query = query.where("sent_by", "==", selectedAgent)
		}
		query = query.orderBy("sent_at", "desc")

		const unsubscribe = query.onSnapshot(
			async (snap) => {
				const enriched = await enrichLinks(snap.docs)
				setRows(enriched)
				setLoading(false)
			},
			(err) => {
				console.error("Payment links listener failed:", err)
				pushAlert({ type: "ERROR", title: err?.message || "Failed to load payment links." })
				setLoading(false)
			},
		)

		return () => unsubscribe()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedAgent, startDate, endDate, isAdmin])

	// Resolve a uid to a display name via cache → live user doc → cached
	// `sent_by_name` fallback. Always prefers the live first/last from the
	// user doc so a backfill takes effect without a re-create of the link.
	const resolveAgentName = async (uid, fallback) => {
		if (!uid) return fallback || "—"
		if (userNameCacheRef.current[uid]) return userNameCacheRef.current[uid]
		try {
			const snap = await db.doc(`users/${uid}`).get()
			const data = snap.data() || {}
			const fullName = [data.first_name, data.last_name].filter(Boolean).join(" ")
			const resolved = fullName || fallback || uid
			userNameCacheRef.current[uid] = resolved
			return resolved
		} catch {
			return fallback || uid
		}
	}

	const enrichLinks = (docs) =>
		Promise.all(
			docs.map(async (linkDoc) => {
				const data = linkDoc.data()
				const parent = linkDoc.ref.parent.parent
				const parentType = parent?.parent?.id // "clients" or "bookings"
				const parentID = parent?.id

				let bookingID = null
				let bookingRef = null
				if (parentType === "bookings" && parentID) {
					bookingID = parentID
					const bookingSnap = await db.doc(`bookings/${parentID}`).get()
					const b = bookingSnap.data() || {}
					bookingRef = b.reference ? String(b.reference) : parentID.slice(-6)
				} else if (data.allocated_booking_id) {
					// Client-level link that's since been allocated onto a booking.
					bookingID = data.allocated_booking_id
					const bookingSnap = await db.doc(`bookings/${data.allocated_booking_id}`).get()
					const b = bookingSnap.data() || {}
					bookingRef = b.reference ? String(b.reference) : data.allocated_booking_id.slice(-6)
				}

				const sentBy = await resolveAgentName(data.sent_by, data.sent_by_name)

				return {
					id: linkDoc.id,
					parentType,
					parentID,
					bookingID,
					bookingRef,
					amount: Number(data.amount || 0),
					payee: data.payee || "—",
					recipient: data.recipient_email || "",
					sentAt: tsToMoment(data.sent_at),
					sentBy,
					status: data.status || "ACTIVE",
					note: data.note,
					url: data.stripe_payment_link_url,
					cancelledAt: tsToMoment(data.cancelled_at),
					paidAt: tsToMoment(data.paid_at),
					lastSentAt: tsToMoment(data.last_sent_at),
					sendCount: data.send_count || 1,
					raw: data,
				}
			}),
		)

	const handleCopy = async (url) => {
		try {
			await navigator.clipboard.writeText(url)
			pushAlert({ type: "SUCCESS", title: "Link copied" })
		} catch {
			pushAlert({ type: "ERROR", title: "Couldn't copy — copy it manually." })
		}
	}

	const handleResend = async (row) => {
		if (row.parentType !== "clients") {
			pushAlert({ type: "ERROR", title: "Resend from this view only supports client-level links." })
			return
		}
		setBusyId(row.id)
		setBusyAction("resend")
		try {
			await resendCallable({ clientID: row.parentID, linkID: row.id })
			pushAlert({ type: "SUCCESS", title: "Payment link re-sent" })
		} catch (err) {
			pushAlert({ type: "ERROR", title: err?.message || "Could not resend." })
		} finally {
			setBusyId(null)
			setBusyAction(null)
		}
	}

	const handleCancel = async (row) => {
		if (row.parentType !== "clients") {
			pushAlert({ type: "ERROR", title: "Cancel from this view only supports client-level links." })
			return
		}
		setBusyId(row.id)
		setBusyAction("cancel")
		try {
			await cancelCallable({ clientID: row.parentID, linkID: row.id })
			pushAlert({ type: "SUCCESS", title: "Payment link cancelled" })
		} catch (err) {
			pushAlert({ type: "ERROR", title: err?.message || "Could not cancel." })
		} finally {
			setBusyId(null)
			setBusyAction(null)
		}
	}

	return (
		<Tile fullPage={true}>
			<Title className="flex">
				<h1>Payment Links</h1>
				<Button
					label="Create payment link"
					icon={<PlusIcon />}
					small={true}
					onClick={() => setCreating(true)}
				/>
			</Title>

			<div className={styles.filters}>
				<div className={styles.filterDate}>
					<Datepicker
						value={startDate}
						onSelect={(date) => setStartDate(date)}
						placeholder="Start date"
					/>
				</div>

				<div className={styles.filterDate}>
					<Datepicker
						value={endDate}
						onSelect={(date) => setEndDate(date)}
						placeholder="End date"
					/>
				</div>

				{isAdmin && Object.keys(agents).length > 0 && (
					<>
						<div className={styles.filterSelect}>
							<Select
								placeholder="Choose agent"
								value=""
								selected={agents[selectedAgent] ? selectedAgent : null}
								options={agents}
								onSelect={(option) => setSelectedAgent(option.option)}
							/>
						</div>

						{selectedAgent && (
							<Button
								label="Clear agent"
								type="secondary"
								small={true}
								onClick={() => setSelectedAgent(null)}
							/>
						)}
					</>
				)}
			</div>

			<Table
				className="bookings-table"
				headings={["Agent", "Sent", "Paid at", "Payee", "Recipient", "Booking ref", "Status", "Amount", ""]}
				noResults={rows.length === 0}
				noResultsMessage={loading ? "Loading payment links..." : "No payment links found in this range. Create one to get started."}>
				{rows.map((row) => {
					const statusMeta = STATUS_META[row.status] || STATUS_META.ACTIVE

					return (
						<tr key={row.id}>
							<td>{row.sentBy || "—"}</td>
							<td>{row.sentAt ? row.sentAt.format("DD/MM/YYYY HH:mm") : "—"}</td>
							<td>{row.paidAt ? row.paidAt.format("DD/MM/YYYY HH:mm") : "—"}</td>
							<td>{row.payee}</td>
							<td>{row.recipient}</td>
							<td>
								{row.bookingID ? (
									<NavLink to={`/booking/${row.bookingID}`}>{row.bookingRef}</NavLink>
								) : (
									<small className="no-value">—</small>
								)}
							</td>
							<td>
								<div className={styles.statusStack}>
									{row.status === "ALLOCATED" && (
										<Badge
											type={STATUS_META.PAID.type}
											label={STATUS_META.PAID.label}
										/>
									)}
									<Badge
										type={statusMeta.type}
										label={statusMeta.label}
									/>
								</div>
							</td>
							<td>£{formatGbp(row.amount)}</td>
							<td className="is-button">
								<div className={styles.rowActions}>
									{row.status === "PAID" && row.parentType === "clients" && (
										<Button
											small={true}
											type="secondary"
											label="Allocate"
											onClick={() => setAllocatingRow(row)}
										/>
									)}
									<Button
										small={true}
										label="View"
										onClick={() => setViewingRow(row)}
									/>
								</div>
							</td>
						</tr>
					)
				})}
			</Table>

			{creating && (
				<CreateClientPaymentLink
					close={() => setCreating(false)}
					onCreated={() => setCreating(false)}
				/>
			)}

			{viewingRow && (
				<PaymentLinkDetail
					row={viewingRow}
					busy={busyId === viewingRow.id}
					busyAction={busyId === viewingRow.id ? busyAction : null}
					onCopy={() => handleCopy(viewingRow.url)}
					onResend={async () => {
						await handleResend(viewingRow)
						setViewingRow(null)
					}}
					onCancel={async () => {
						await handleCancel(viewingRow)
						setViewingRow(null)
					}}
					close={() => setViewingRow(null)}
				/>
			)}

			{allocatingRow && (
				<ApplyCreditToBooking
					clientID={allocatingRow.parentID}
					paymentID={allocatingRow.raw.paid_payment_id}
					linkID={allocatingRow.id}
					amount={allocatingRow.amount}
					reference={`Payment link ${allocatingRow.payee} - £${formatGbp(allocatingRow.amount)}`}
					close={() => setAllocatingRow(null)}
				/>
			)}
		</Tile>
	)
}
